/* eslint-disable @typescript-eslint/ban-ts-comment */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { getCookie, setCookie } from "cookies-next/client";
import { PRODUCT_NAME_CAR } from "@/constants/productTypes";
import { SelectedAccessories } from "@/@types/accessories";
import { FreebieProduct } from "@/@types/freebies";
import { createSafeStorage } from "@/lib/utils";
import type { HomepageResponse } from "@/app/_actions/homepage";
import type { RewardProgramData } from "@/app/product/_actions/user.actions";

export type RecentProduct = {
	variantId: string;
	type: "device" | "car";
	subtype?: string;
	image?: string;
	href: string;
	model?: string;
	manufacturer?: string;
	timestamp: number;
};

// ------------------ Types ------------------
export type AppData = {
	variantId: string;
	cityId?: string;
	ctc: number;
	taxRegime: "old" | "new";
	includeInsurance: boolean;
	addExpenses: boolean;
	driver: number | null;
	fuel: number | null;
	maintenance: number | null;
	phoneNumber?: number;
	email: string;
	personalEmail: string;
	firstName: string; // Added firstName
	tenures: number[];
	deviceId: string;
	authorization: string;
	selectedCarTenure: number;
	selectedDeviceTenure: number;
	defaultCarTenure?: number;
	defaultDeviceTenure?: number;
	tenuresCar?: number[];
	tenuresDevice?: number[];
	carVariantId: string;
	onboardingCompleted: boolean;
	onboardingCompletionType: ProductName;
	residualPct: number;
	isCapturing: boolean;
	showCar: boolean;
	showDevice: boolean;
	showToggle: boolean;
	filteredProductList: FilteredListProduct[];
	selectedFilters: {
		[key: string]: string[];
	};
	selectedCategories?: string[]; // Added for category filtering
	deviceSubtypes?: { mobile: boolean; tablet: boolean; laptop: boolean };
	d: string;
	domain: string;
	showEligibleProducts: boolean;
	showBuybackEligibleProducts: boolean;
	showOnOffer: boolean;
	workflowResponse: Workflow | null;
	redirectUrl: string | null;
	currentTenureLease: LeaseTenure | null;
	orderErrorMessage: string;
	accessories: SelectedAccessories;
	freebie: FreebieProduct | null;
	hasAcceptedOnBoardingConsent?: boolean; // Flag for onboarding consent
	hasAcceptedAddressConsent?: boolean; // Flag for address consent
	isOnboardingConsentEnabled?: boolean; // Flag to control if onboarding consent is enabled (from config)
	isAddressConsentEnabled?: boolean; // Flag to control if address consent is enabled (from config)
	rewardsPopupSeen?: boolean;
	cityList?: City[] | [];
	user?: User | null;
	isUserReady?: boolean;
	isExistingUser?: boolean;
	companyBanners?: BannerItem[];
	eligibilityCaps?: EligibilityCapsResponse | null;
	restrictDeviceCtcView?: boolean;
	financeChargesEnabled?: boolean;
	showCtc?: boolean;
	deliverToOfficeAddress?: boolean;
	buybackConfig?: BuybackConfig | null;
	fuelDriverMaintenance?: FuelDriverMaintenance | null;
	fdmHintDismissed?: boolean;
	companyDefaultFreebies?: FreebieProduct[];
	freebieRules?: { accessoryCount?: number; minCartValue?: number };
	insuranceYears: number;
	availableInsuranceYears?: number[];
	insuranceRates?: (number | null)[];
	allowedAccessoryTypes?: string[];
	allowedAccessoryCount?: number | null;
	// PLP accessory browsing: null/undefined = not in accessory mode; a subtype
	// key (e.g. "watch") = accessory mode with that sub-category selected.
	selectedAccessoryType?: string | null;
	// Set when navigating from the PLP to a device PDP so the accessories modal
	// auto-opens this specific accessory's detail drawer, then cleared.
	openAccessoryDetailId?: string | null;
	homepageBannerCar?: HomepageBanner | null;
	homepageBannerDevice?: HomepageBanner | null;
	homepageData?: HomepageResponse | null;
	recentlyViewedProducts?: RecentProduct[];
	featuredCars?: Car[];
	featuredDevices?: Car[];
	featuredLeaseCache?: Record<string, Lease>;
	featuredLoanCache?: Record<string, Loan>;
	rewardProgramData?: RewardProgramData | null;
};

export type AppStore = {
	data: AppData;
	hasHydrated: boolean;
	setData: (values: Partial<AppData>) => void;
	setCookielessData: (values: Partial<AppData>) => void;
	syncWithCookies: () => void;
	setHasHydrated: (value: boolean) => void;
	getAccessories: () => SelectedAccessories;
	setAccessories: (accessories: SelectedAccessories) => void;
};

type CookieData = AppData & {
	selectedFilters?: never;
	selectedCategories?: never;
	filteredProductList: never;
	showEligibleProducts: never;
	showBuybackEligibleProducts: never;
	showOnOffer: never;
	workflow: never;
	redirectUrl: never;
	currentTenureLease: never;
	orderErrorMessage: never;
	cityList: never;
	user: never;
	isUserReady: never;
	isExistingUser: never;
	companyBanners: never;
	restrictDeviceCtcView: never;
	financeChargesEnabled: never;
	showCtc: never;
	deliverToOfficeAddress: never;
	buybackConfig: never;
	fuelDriverMaintenance: never;
	availableInsuranceYears: never;
	insuranceRates: never;
	allowedAccessoryTypes: never;
	allowedAccessoryCount: never;
	selectedAccessoryType: never;
	openAccessoryDetailId: never;
	homepageBannerCar: never;
	homepageBannerDevice: never;
	homepageData: never;
	recentlyViewedProducts: never;
	featuredCars: never;
	featuredDevices: never;
	featuredLeaseCache: never;
	featuredLoanCache: never;
	rewardProgramData: never;
};

// ------------------ Constants ------------------
const COOKIE_KEYS: Record<keyof CookieData, string> = {
	variantId: "variantId",
	cityId: "cityId",
	ctc: "salary",
	taxRegime: "taxRegime",
	includeInsurance: "includeInsurance",
	addExpenses: "addExpenses",
	driver: "driverExpense",
	fuel: "fuelExpense",
	maintenance: "maintenanceExpense",
	phoneNumber: "phoneNumber",
	email: "email",
	personalEmail: "personalEmail",
	tenures: "tenures",
	deviceId: "deviceId",
	authorization: "Authorization",
	selectedCarTenure: "selectedCarTenure",
	selectedDeviceTenure: "selectedDeviceTenure",
	carVariantId: "carVariantId",
	onboardingCompleted: "onboardingCompleted",
	onboardingCompletionType: "onboardingCompletionType",
	residualPct: "residualPct",
	isCapturing: "isCapturing",
	d: "d",
	domain: "domain",
	accessories: "accessories",
	freebie: "freebie",
	firstName: "firstName",
	fdmHintDismissed: "fdmHintDismissed",
	hasAcceptedOnBoardingConsent: "hasAcceptedOnBoardingConsent",
	rewardsPopupSeen: "rewardsPopupSeen",
	insuranceYears: "insuranceYears",
};

const DEFAULT_VALUES: AppData = {
	variantId: "",
	cityId: "",
	selectedCarTenure: 0,
	selectedDeviceTenure: 0,
	ctc: 0,
	taxRegime: "new",
	includeInsurance: false,
	addExpenses: false,
	driver: null,
	fuel: null,
	maintenance: null,
	email: "sam@example.com",
	personalEmail: "sam@example.com",
	firstName: "",
	tenures: [6, 9, 12, 24, 36, 48],
	deviceId: "",
	authorization: "",
	carVariantId: "",
	onboardingCompleted: false,
	onboardingCompletionType: PRODUCT_NAME_CAR,
	residualPct: 10,
	isCapturing: false,
	showCar: true,
	showDevice: true,
	showToggle: true,
	selectedFilters: {},
	selectedCategories: ["mobile"], // Devices always have one category (default Mobiles)
	selectedAccessoryType: null,
	openAccessoryDetailId: null,
	deviceSubtypes: { mobile: true, tablet: true, laptop: true },
	filteredProductList: [],
	d: "",
	domain: "",
	showEligibleProducts: false,
	showBuybackEligibleProducts: false,
	showOnOffer: false,
	workflowResponse: null,
	redirectUrl: null,
	currentTenureLease: null,
	orderErrorMessage: "",
	accessories: {},
	freebie: null,
	hasAcceptedOnBoardingConsent: false,
	hasAcceptedAddressConsent: false,
	rewardsPopupSeen: false,
	fdmHintDismissed: false,
	insuranceYears: 1,
	availableInsuranceYears: [1],
	insuranceRates: [],
	isOnboardingConsentEnabled: false, // Default to enabled
	isAddressConsentEnabled: false, // Default to enabled
	cityList: [],
	user: null,
	isUserReady: false,
	isExistingUser: false,
	eligibilityCaps: null,
	companyDefaultFreebies: [],
};

// ------------------ Helpers ------------------
function parseCookieValue<K extends keyof AppData>(
	key: K,
	value: string | undefined,
): AppData[K] {
	if (value === undefined) return DEFAULT_VALUES[key];

	switch (key) {
		case "selectedCarTenure":
		case "selectedDeviceTenure":
		case "ctc":
		case "driver":
		case "fuel":
		case "maintenance":
		case "phoneNumber":
		case "residualPct":
		case "insuranceYears": {
			const num = Number(value);
			return (Number.isNaN(num) ? DEFAULT_VALUES[key] : num) as AppData[K];
		}
		case "addExpenses":
		case "includeInsurance":
		case "onboardingCompleted":
		case "rewardsPopupSeen":
		case "fdmHintDismissed": {
			return (value?.toLowerCase() === "true") as AppData[K];
		}
		case "onboardingCompletionType": {
			if (value === "cars" || value === "devices") {
				return value as AppData[K];
			}

			if (value === undefined) {
				const legacy = getCookie("bookingType");
				if (legacy === "car") return "cars" as AppData[K];
				if (legacy === "mobile") return "devices" as AppData[K];
			}
			return DEFAULT_VALUES[key];
		}
		case "tenures": {
			try {
				return JSON.parse(value) as AppData[K];
			} catch {
				return DEFAULT_VALUES[key];
			}
		}
		case "accessories": {
			try {
				const parsed = JSON.parse(value);
				// Compact format written by setData: values are plain id strings
				// e.g. { "cover": "acc-id-123" }
				const firstVal = Object.values(parsed)[0];
				if (typeof firstVal === "string") {
					// Reconstruct minimal Accessory objects so acc.id is available
					// for the booking flow. Full data (name/image/price) is in localStorage.
					return Object.fromEntries(
						Object.entries(parsed as Record<string, string>).map(
							([categoryId, id]) => [categoryId, { id, categoryId }],
						),
					) as AppData[K];
				}
				// Legacy full-object format — return as-is
				return parsed as AppData[K];
			} catch {
				return DEFAULT_VALUES[key];
			}
		}
		case "freebie": {
			try {
				return JSON.parse(value) as AppData[K];
			} catch {
				return DEFAULT_VALUES[key];
			}
		}
		case "variantId":
		case "carVariantId":
		case "taxRegime":
		case "cityId":
		case "email":
		case "personalEmail":
		case "deviceId":
		case "authorization":
		case "d":
		case "domain":
		case "firstName":
			return value as AppData[K];
		default:
			return DEFAULT_VALUES[key];
	}
}

export function getInitialState(): AppData {
	if (typeof window === "undefined") return DEFAULT_VALUES;

	return Object.fromEntries(
		(Object.keys(COOKIE_KEYS) as (keyof AppData)[]).map((key) => {
			const cookieVal = getCookie(COOKIE_KEYS[key]);
			return [key, parseCookieValue(key, cookieVal)];
		}),
	) as AppData;
}

// ------------------ Store Definition ------------------
export const useAppStore = create<AppStore>()(
	persist(
		(set, get) => ({
			data: DEFAULT_VALUES,
			hasHydrated: false,

			setData: (values) => {
				set((state) => {
					const updated = { ...state.data, ...values };
					Object.entries(values).forEach(([k, v]) => {
						if (v !== undefined) {
							const cookieKey = COOKIE_KEYS[k as keyof AppData];
							if (cookieKey) {
								let valueToStore: string;
								if (
									k === "accessories" &&
									typeof v === "object" &&
									v !== null
								) {
									// Store only { [categoryId]: accessory.id } to stay
									// well under the 4 KB cookie limit regardless of how
									// many accessories the user selects.
									const compact = Object.fromEntries(
										Object.entries(v as SelectedAccessories).map(
											([catId, acc]) => [catId, acc.id],
										),
									);
									valueToStore = JSON.stringify(compact);
								} else {
									valueToStore =
										typeof v === "boolean"
											? String(v)
											: typeof v === "object" && v !== null
												? JSON.stringify(v)
												: String(v);
								}
								setCookie(cookieKey, valueToStore, {
									maxAge: 60 * 60 * 24 * 30,
									path: "/",
									sameSite: "lax" as const,
								});
							}
						}
					});

					return { data: updated };
				});
			},

			setCookielessData: (values) => {
				set((state) => ({
					data: { ...state.data, ...values },
				}));
			},

			syncWithCookies: () => {
				const updates: { [key in keyof AppData]?: AppData[key] } = {};
				const current = get().data;
				let changed = false;

				(Object.keys(COOKIE_KEYS) as (keyof AppData)[]).forEach((key) => {
					const rawCookie = getCookie(COOKIE_KEYS[key]);
					// Skip keys where the cookie isn't set — don't let defaults
					// clobber already-hydrated state (e.g. a user toggle).
					if (rawCookie === undefined || rawCookie === null) return;
					const val = parseCookieValue(key, rawCookie as string);

					// Accessories are stored in compact form in the cookie (just IDs).
					// Never let the cookie overwrite a richer in-memory/localStorage
					// state that has more selections — the cookie may be stale or have
					// been written before a 5th+ accessory was added.
					if (key === "accessories") {
						const cookieCount = Object.keys(
							(val as SelectedAccessories) ?? {},
						).length;
						const currentCount = Object.keys(
							(current.accessories as SelectedAccessories) ?? {},
						).length;
						if (currentCount >= cookieCount) return;
						// Cookie somehow has more entries (e.g. fresh localStorage) — fall through
					}

					if (current[key] !== val) {
						//@ts-ignore
						updates[key] = val as AppData[key];
						changed = true;
					}
				});

				if (changed) {
					set((state) => ({
						data: { ...state.data, ...updates },
					}));
				}
			},

			setHasHydrated: (value) => set({ hasHydrated: value }),

			getAccessories: () => get().data.accessories,

			setAccessories: (accessories) => get().setData({ accessories }),
		}),
		{
			name: "automint-storage",
			storage: createJSONStorage(() => createSafeStorage()),
			// cityList and the featured-products fields are deliberately excluded:
			// they must always come from their live fetches (CityListInitializer /
			// product-tabs), never from a previous session's cache. Persisting
			// cityList caused product-tabs' effect to double-fire (see git history);
			// persisting the featured fields caused stale featured cars/devices from
			// the last session to flash on screen before the live fetch overwrites
			// them - same root cause, different symptom (a visible reload/blink
			// instead of a duplicate network call).
			partialize: (state) => {
				const rest: Partial<AppData> = { ...state.data };
				delete rest.cityList;
				delete rest.featuredCars;
				delete rest.featuredDevices;
				delete rest.featuredLeaseCache;
				delete rest.featuredLoanCache;
				// Everything useConfig's fetchConfig() sets via setCookielessData
				// (see hooks/useConfig.ts) is employer/company-scoped, resolved fresh
				// from the current auth state on every load - persisting it lets a
				// previous identity's (or a guest's) values survive a login/logout on
				// the same browser and flash on screen (or worse, stay wrong) until
				// the live fetch overwrites them. Same root cause as the product
				// fields above, just for config instead of product data.
				delete rest.showCar;
				delete rest.showDevice;
				delete rest.showToggle;
				delete rest.deviceSubtypes;
				delete rest.defaultCarTenure;
				delete rest.defaultDeviceTenure;
				delete rest.tenuresCar;
				delete rest.tenuresDevice;
				delete rest.isOnboardingConsentEnabled;
				delete rest.isAddressConsentEnabled;
				delete rest.companyBanners;
				delete rest.restrictDeviceCtcView;
				delete rest.financeChargesEnabled;
				delete rest.showCtc;
				delete rest.deliverToOfficeAddress;
				delete rest.buybackConfig;
				delete rest.fuelDriverMaintenance;
				delete rest.companyDefaultFreebies;
				delete rest.freebieRules;
				delete rest.availableInsuranceYears;
				delete rest.insuranceRates;
				delete rest.allowedAccessoryTypes;
				delete rest.allowedAccessoryCount;
				delete rest.homepageBannerCar;
				delete rest.homepageBannerDevice;
				return { data: rest };
			},
			merge: (persisted, current) => {
				const persistedData = (
					persisted as { data?: Partial<AppData> } | undefined
				)?.data;
				return {
					...current,
					data: { ...DEFAULT_VALUES, ...(persistedData ?? {}) },
				};
			},
			onRehydrateStorage: () => (state) => {
				if (state) {
					state.syncWithCookies();
					state.setHasHydrated(true);
				}
			},
		},
	),
);

// Client-only sync
if (typeof window !== "undefined") {
	useAppStore.getState().syncWithCookies();
}

// ------------------ Hook ------------------
export function useAppData(): [
	AppData,
	(values: Partial<AppData>) => void,
	(values: Partial<AppData>) => void,
	boolean,
	() => SelectedAccessories,
	(accessories: SelectedAccessories) => void,
] {
	const data = useAppStore((s) => s.data);
	const setData = useAppStore((s) => s.setData);
	const setCookielessData = useAppStore((s) => s.setCookielessData);
	const hasHydrated = useAppStore((s) => s.hasHydrated);
	const getAccessories = useAppStore((s) => s.getAccessories);
	const setAccessories = useAppStore((s) => s.setAccessories);
	return [
		data,
		setData,
		setCookielessData,
		hasHydrated,
		getAccessories,
		setAccessories,
	];
}
